import { queryOptions } from '@tanstack/react-query';
import type { PaginationParams } from '../model';
import {
  merchantApi,
  modifierApi,
  orderApi,
  productApi,
  productBannerApi,
  productCategoryApi,
  promotionApi,
  restaurantApi,
  restaurantCategoryApi,
  restaurantBannerApi,
  reviewApi,
  employeeApi,
  type ListBannersParams,
  type ListMerchantPromotionsParams,
  type ListEmployeesParams,
  type ListMerchantsParams,
  type ListModifierGroupsParams,
  type ListOrdersParams,
  type ListProductCategoriesParams,
  type ListMerchantProductsByRestaurantParams,
  type ListMerchantProductsParams,
  type ListPromotionsByRestaurantParams,
  type ListRestaurantsParams,
  type ListRestaurantCategoriesParams,
  type ListPublishedProductsParams,
  type ListPublishedCategoriesParams,
  type ListPublishedPromotionsParams,
  type ListPublishedRestaurantsParams,
  type OrderDateRange,
} from './index';

// One query-key namespace for the whole Eats merchant API, split per service so
// invalidation can target a service (`eatsKeys.orders.all`) or an exact query.
// Params objects are folded into the key so distinct filters cache separately.

export const eatsKeys = {
  all: ['eats'] as const,

  merchants: {
    all: ['eats', 'merchants'] as const,
    capabilities: () => ['eats', 'merchants', 'me', 'capabilities'] as const,
    realtimeChannels: () => ['eats', 'merchants', 'me', 'realtime'] as const,
    list: (params?: ListMerchantsParams) =>
      ['eats', 'merchants', 'list', params ?? {}] as const,
    detail: (id: string) => ['eats', 'merchants', 'detail', id] as const,
  },

  restaurants: {
    all: ['eats', 'restaurants'] as const,
    list: (params?: ListRestaurantsParams) =>
      ['eats', 'restaurants', 'list', params ?? {}] as const,
    detail: (id: string) => ['eats', 'restaurants', 'detail', id] as const,
    deliveryPricing: (id: string) =>
      ['eats', 'restaurants', 'delivery-pricing', id] as const,
    localizedWithSchedule: (id: string) =>
      ['eats', 'restaurants', 'localized-with-schedule', id] as const,
  },

  restaurantCategories: {
    all: ['eats', 'restaurant-categories'] as const,
    list: (params?: ListRestaurantCategoriesParams) =>
      ['eats', 'restaurant-categories', 'list', params ?? {}] as const,
  },

  products: {
    all: ['eats', 'products'] as const,
    list: (params?: ListMerchantProductsParams) =>
      ['eats', 'products', 'list', params ?? {}] as const,
    byRestaurant: (id: string, params?: ListMerchantProductsByRestaurantParams) =>
      ['eats', 'products', 'by-restaurant', id, params ?? {}] as const,
    top: (params: { limit?: number }) => ['eats', 'products', 'top', params] as const,
    detail: (id: string) => ['eats', 'products', 'detail', id] as const,
    detailByRestaurant: (id: string, restaurantId: string) =>
      ['eats', 'products', 'detail', id, 'by-restaurant', restaurantId] as const,
  },

  productCategories: {
    all: ['eats', 'product-categories'] as const,
    list: (params?: ListProductCategoriesParams) =>
      ['eats', 'product-categories', 'list', params ?? {}] as const,
    detail: (id: string) => ['eats', 'product-categories', 'detail', id] as const,
  },

  modifierGroups: {
    all: ['eats', 'modifier-groups'] as const,
    list: (params?: ListModifierGroupsParams) =>
      ['eats', 'modifier-groups', 'list', params ?? {}] as const,
    detail: (id: string) => ['eats', 'modifier-groups', 'detail', id] as const,
  },

  promotions: {
    all: ['eats', 'promotions'] as const,
    byMerchant: (params?: ListMerchantPromotionsParams) =>
      ['eats', 'promotions', 'by-merchant', params ?? {}] as const,
    byRestaurant: (restaurantId: string, params?: ListPromotionsByRestaurantParams) =>
      ['eats', 'promotions', 'by-restaurant', restaurantId, params ?? {}] as const,
    /** Branch-scoped: the read requires a branch and its answer varies by one. */
    detail: (id: string, restaurantId: string) =>
      ['eats', 'promotions', 'detail', id, restaurantId] as const,
  },

  orders: {
    all: ['eats', 'orders'] as const,
    byMerchant: (params?: ListOrdersParams) =>
      ['eats', 'orders', 'by-merchant', params ?? {}] as const,
    byRestaurant: (restaurantId: string, params?: ListOrdersParams) =>
      ['eats', 'orders', 'by-restaurant', restaurantId, params ?? {}] as const,
    detail: (id: string, restaurantId?: string) =>
      ['eats', 'orders', 'detail', id, restaurantId ?? ''] as const,
    weekdayCountsByMerchant: (range: OrderDateRange) =>
      ['eats', 'orders', 'weekday-counts', 'by-merchant', range] as const,
    weekdayCountsByRestaurant: (restaurantId: string, range: OrderDateRange) =>
      ['eats', 'orders', 'weekday-counts', 'by-restaurant', restaurantId, range] as const,
  },

  reviews: {
    all: ['eats', 'reviews'] as const,
    byMerchant: (params?: PaginationParams) =>
      ['eats', 'reviews', 'by-merchant', params ?? {}] as const,
    byRestaurant: (restaurantId: string, params?: PaginationParams) =>
      ['eats', 'reviews', 'by-restaurant', restaurantId, params ?? {}] as const,
  },

  employees: {
    all: ['eats', 'employees'] as const,
    list: (params?: ListEmployeesParams) =>
      ['eats', 'employees', 'list', params ?? {}] as const,
    detail: (id: string) => ['eats', 'employees', 'detail', id] as const,
  },

  banners: {
    all: ['eats', 'banners'] as const,
    restaurantList: (params?: ListBannersParams) =>
      ['eats', 'banners', 'restaurant', 'list', params ?? {}] as const,
    restaurantDetail: (id: string) =>
      ['eats', 'banners', 'restaurant', 'detail', id] as const,
    productList: (params?: ListBannersParams) =>
      ['eats', 'banners', 'product', 'list', params ?? {}] as const,
    productDetail: (id: string) => ['eats', 'banners', 'product', 'detail', id] as const,
  },

  published: {
    all: ['eats', 'published'] as const,
    products: (params?: ListPublishedProductsParams) =>
      ['eats', 'published', 'products', params ?? {}] as const,
    productDetail: (id: string, restaurantId?: string) =>
      ['eats', 'published', 'products', 'detail', id, restaurantId ?? ''] as const,
    categories: (params?: ListPublishedCategoriesParams) =>
      ['eats', 'published', 'categories', params ?? {}] as const,
    categoryDetail: (id: string) =>
      ['eats', 'published', 'categories', 'detail', id] as const,
    promotions: (params?: ListPublishedPromotionsParams) =>
      ['eats', 'published', 'promotions', params ?? {}] as const,
    promotionDetail: (id: string, restaurantId?: string) =>
      ['eats', 'published', 'promotions', 'detail', id, restaurantId ?? ''] as const,
    restaurants: (params?: ListPublishedRestaurantsParams) =>
      ['eats', 'published', 'restaurants', params ?? {}] as const,
    restaurantDetail: (id: string) =>
      ['eats', 'published', 'restaurants', 'detail', id] as const,
  },
} as const;

/**
 * `queryOptions` factories for every read endpoint. Each `queryFn` receives the
 * `AbortSignal` from its context so TanStack can cancel in-flight requests.
 */
export const eatsQueries = {
  // ── Merchants ────────────────────────────────────────────────────────────
  merchantCapabilities: () =>
    queryOptions({
      queryKey: eatsKeys.merchants.capabilities(),
      queryFn: ({ signal }) => merchantApi.capabilities({ signal }),
    }),
  /**
   * The merchant's realtime channels. Long-lived but NOT infinite: a new
   * channel is a backend change to this endpoint, so a fresh mount re-reads
   * it rather than trusting a stale cached list.
   */
  merchantRealtimeChannels: () =>
    queryOptions({
      queryKey: eatsKeys.merchants.realtimeChannels(),
      queryFn: ({ signal }) => merchantApi.realtimeChannels({ signal }),
    }),
  merchantList: (params?: ListMerchantsParams) =>
    queryOptions({
      queryKey: eatsKeys.merchants.list(params),
      queryFn: ({ signal }) => merchantApi.list(params, { signal }),
    }),
  merchant: (id: string) =>
    queryOptions({
      queryKey: eatsKeys.merchants.detail(id),
      queryFn: ({ signal }) => merchantApi.get(id, { signal }),
    }),

  // ── Restaurants (branches) ───────────────────────────────────────────────
  restaurantList: (params?: ListRestaurantsParams) =>
    queryOptions({
      queryKey: eatsKeys.restaurants.list(params),
      queryFn: ({ signal }) => restaurantApi.list(params, { signal }),
    }),
  restaurant: (id: string) =>
    queryOptions({
      queryKey: eatsKeys.restaurants.detail(id),
      queryFn: ({ signal }) => restaurantApi.get(id, { signal }),
    }),
  restaurantDeliveryPricing: (id: string) =>
    queryOptions({
      queryKey: eatsKeys.restaurants.deliveryPricing(id),
      queryFn: ({ signal }) => restaurantApi.getDeliveryPricing(id, { signal }),
    }),
  /**
   * One branch with EVERY translation plus its weekly schedule in a single
   * call — `GET /restaurants/{id}/by-merchant`. Seeds read-only detail views
   * (the showcase "Режим просмотра" drawer) without opening the editor.
   */
  restaurantLocalizedWithSchedule: (id: string) =>
    queryOptions({
      queryKey: eatsKeys.restaurants.localizedWithSchedule(id),
      queryFn: ({ signal }) => restaurantApi.getLocalizedWithSchedule(id, { signal }),
    }),
  restaurantCategoryList: (params?: ListRestaurantCategoriesParams) =>
    queryOptions({
      queryKey: eatsKeys.restaurantCategories.list(params),
      queryFn: ({ signal }) => restaurantCategoryApi.list(params, { signal }),
    }),

  // ── Products ─────────────────────────────────────────────────────────────
  productList: (params?: ListMerchantProductsParams) =>
    queryOptions({
      queryKey: eatsKeys.products.list(params),
      queryFn: ({ signal }) => productApi.list(params, { signal }),
    }),
  productsByRestaurant: (id: string, params?: ListMerchantProductsByRestaurantParams) =>
    queryOptions({
      queryKey: eatsKeys.products.byRestaurant(id, params),
      queryFn: ({ signal }) => productApi.listByRestaurant(id, params, { signal }),
    }),
  topProducts: (params: { limit?: number }) =>
    queryOptions({
      queryKey: eatsKeys.products.top(params),
      queryFn: ({ signal }) => productApi.listTop(params, { signal }),
    }),
  product: (id: string) =>
    queryOptions({
      queryKey: eatsKeys.products.detail(id),
      queryFn: ({ signal }) => productApi.get(id, { signal }),
    }),
  productByRestaurant: (id: string, restaurantId: string) =>
    queryOptions({
      queryKey: eatsKeys.products.detailByRestaurant(id, restaurantId),
      queryFn: ({ signal }) => productApi.getByRestaurant(id, restaurantId, { signal }),
    }),

  // ── Product categories ───────────────────────────────────────────────────
  productCategoryList: (params?: ListProductCategoriesParams) =>
    queryOptions({
      queryKey: eatsKeys.productCategories.list(params),
      queryFn: ({ signal }) => productCategoryApi.list(params, { signal }),
    }),
  productCategory: (id: string) =>
    queryOptions({
      queryKey: eatsKeys.productCategories.detail(id),
      queryFn: ({ signal }) => productCategoryApi.get(id, { signal }),
    }),

  // ── Modifier groups (catalogue, v1.00.227) ───────────────────────────────
  modifierGroupList: (params?: ListModifierGroupsParams) =>
    queryOptions({
      queryKey: eatsKeys.modifierGroups.list(params),
      queryFn: ({ signal }) => modifierApi.list(params, { signal }),
    }),
  modifierGroup: (id: string) =>
    queryOptions({
      queryKey: eatsKeys.modifierGroups.detail(id),
      queryFn: ({ signal }) => modifierApi.get(id, { signal }),
    }),

  // ── Promotions ───────────────────────────────────────────────────────────
  merchantPromotions: (params?: ListMerchantPromotionsParams) =>
    queryOptions({
      queryKey: eatsKeys.promotions.byMerchant(params),
      queryFn: ({ signal }) => promotionApi.listByMerchant(params, { signal }),
    }),
  restaurantPromotions: (
    restaurantId: string,
    params?: ListPromotionsByRestaurantParams,
  ) =>
    queryOptions({
      queryKey: eatsKeys.promotions.byRestaurant(restaurantId, params),
      queryFn: ({ signal }) =>
        promotionApi.listByRestaurant(restaurantId, params, { signal }),
    }),
  /**
   * One promotion AT ONE BRANCH. `restaurantId` is required by the endpoint
   * (a call without it 400s) and scopes `product_ids` to that branch, so it is
   * part of the cache key too.
   */
  promotion: (id: string, restaurantId: string) =>
    queryOptions({
      queryKey: eatsKeys.promotions.detail(id, restaurantId),
      queryFn: ({ signal }) => promotionApi.get(id, restaurantId, { signal }),
    }),

  // ── Orders ───────────────────────────────────────────────────────────────
  merchantOrders: (params?: ListOrdersParams) =>
    queryOptions({
      queryKey: eatsKeys.orders.byMerchant(params),
      queryFn: ({ signal }) => orderApi.listByMerchant(params, { signal }),
    }),
  restaurantOrders: (restaurantId: string, params?: ListOrdersParams) =>
    queryOptions({
      queryKey: eatsKeys.orders.byRestaurant(restaurantId, params),
      queryFn: ({ signal }) =>
        orderApi.listByRestaurant(restaurantId, params, { signal }),
    }),
  order: (id: string, restaurantId?: string) =>
    queryOptions({
      queryKey: eatsKeys.orders.detail(id, restaurantId),
      queryFn: ({ signal }) => orderApi.get(id, restaurantId, { signal }),
    }),
  merchantWeekdayCounts: (range: OrderDateRange) =>
    queryOptions({
      queryKey: eatsKeys.orders.weekdayCountsByMerchant(range),
      queryFn: ({ signal }) => orderApi.weekdayCountsByMerchant(range, { signal }),
    }),
  restaurantWeekdayCounts: (restaurantId: string, range: OrderDateRange) =>
    queryOptions({
      queryKey: eatsKeys.orders.weekdayCountsByRestaurant(restaurantId, range),
      queryFn: ({ signal }) =>
        orderApi.weekdayCountsByRestaurant(restaurantId, range, { signal }),
    }),

  // ── Reviews ──────────────────────────────────────────────────────────────
  merchantReviews: (params?: PaginationParams) =>
    queryOptions({
      queryKey: eatsKeys.reviews.byMerchant(params),
      queryFn: ({ signal }) => reviewApi.listByMerchant(params, { signal }),
    }),
  restaurantReviews: (restaurantId: string, params?: PaginationParams) =>
    queryOptions({
      queryKey: eatsKeys.reviews.byRestaurant(restaurantId, params),
      queryFn: ({ signal }) =>
        reviewApi.listByRestaurant(restaurantId, params, { signal }),
    }),

  // ── Employees (staff roster) ─────────────────────────────────────────────
  employeeList: (params?: ListEmployeesParams) =>
    queryOptions({
      queryKey: eatsKeys.employees.list(params),
      queryFn: ({ signal }) => employeeApi.list(params, { signal }),
    }),
  employee: (id: string) =>
    queryOptions({
      queryKey: eatsKeys.employees.detail(id),
      queryFn: ({ signal }) => employeeApi.get(id, { signal }),
    }),
  // ── Banners ──────────────────────────────────────────────────────────────
  restaurantBannerList: (params?: ListBannersParams) =>
    queryOptions({
      queryKey: eatsKeys.banners.restaurantList(params),
      queryFn: ({ signal }) => restaurantBannerApi.list(params, { signal }),
    }),
  restaurantBanner: (id: string) =>
    queryOptions({
      queryKey: eatsKeys.banners.restaurantDetail(id),
      queryFn: ({ signal }) => restaurantBannerApi.get(id, { signal }),
    }),
  productBannerList: (params?: ListBannersParams) =>
    queryOptions({
      queryKey: eatsKeys.banners.productList(params),
      queryFn: ({ signal }) => productBannerApi.list(params, { signal }),
    }),
  productBanner: (id: string) =>
    queryOptions({
      queryKey: eatsKeys.banners.productDetail(id),
      queryFn: ({ signal }) => productBannerApi.get(id, { signal }),
    }),

  // ── Published (Showcase / Режим витрина) ─────────────────────────────────
  publishedProducts: (params?: ListPublishedProductsParams) =>
    queryOptions({
      queryKey: eatsKeys.published.products(params),
      queryFn: ({ signal }) => productApi.listPublished(params, { signal }),
    }),
  publishedProduct: (id: string, restaurantId?: string) =>
    queryOptions({
      queryKey: eatsKeys.published.productDetail(id, restaurantId),
      queryFn: ({ signal }) =>
        productApi.getPublished(
          id,
          restaurantId ? { restaurant_id: restaurantId } : undefined,
          { signal },
        ),
    }),
  publishedCategories: (params?: ListPublishedCategoriesParams) =>
    queryOptions({
      queryKey: eatsKeys.published.categories(params),
      queryFn: ({ signal }) => productCategoryApi.listPublished(params, { signal }),
    }),
  publishedCategory: (id: string) =>
    queryOptions({
      queryKey: eatsKeys.published.categoryDetail(id),
      queryFn: ({ signal }) => productCategoryApi.getPublished(id, { signal }),
    }),
  publishedPromotions: (params?: ListPublishedPromotionsParams) =>
    queryOptions({
      queryKey: eatsKeys.published.promotions(params),
      queryFn: ({ signal }) => promotionApi.listPublished(params, { signal }),
    }),
  publishedPromotion: (id: string, restaurantId?: string) =>
    queryOptions({
      queryKey: eatsKeys.published.promotionDetail(id, restaurantId),
      queryFn: ({ signal }) =>
        promotionApi.getPublished(
          id,
          restaurantId ? { restaurant_id: restaurantId } : undefined,
          { signal },
        ),
    }),
  publishedRestaurants: (params?: ListPublishedRestaurantsParams) =>
    queryOptions({
      queryKey: eatsKeys.published.restaurants(params),
      queryFn: ({ signal }) => restaurantApi.listPublished(params, { signal }),
    }),
  publishedRestaurant: (id: string) =>
    queryOptions({
      queryKey: eatsKeys.published.restaurantDetail(id),
      queryFn: ({ signal }) => restaurantApi.getPublished(id, { signal }),
    }),
} as const;
